import { create } from 'zustand';
import { Case } from '../types';
import { cases } from '../data/sampleData';
import { taskTemplates } from '../data/taskTemplates';
import { calculateDueDate } from '../utils/dates';

interface CaseStore {
  cases: Case[];
  addCase: (caseData: Omit<Case, 'id' | 'documents' | 'tasks' | 'timeline'>) => void;
  updateCase: (id: string, updates: Partial<Case>) => void;
  initializeStore: () => void;
}

export const useCaseStore = create<CaseStore>((set, get) => ({
  cases: [],
  initializeStore: () => {
    // Initialize all cases with their stage-specific tasks
    const initializedCases = cases.map(case_ => {
      // Get all task templates for all stages up to and including the current stage
      const allTasks = Object.entries(taskTemplates)
        .map(([stageId, templates]) => 
          templates.map(template => ({
            ...template,
            id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            dueDate: typeof template.dueDate === 'string' 
              ? template.dueDate 
              : calculateDueDate(template.dueDate),
            assignedTo: case_.assignedLawyerId,
          }))
        )
        .flat();

      return {
        ...case_,
        tasks: allTasks,
      };
    });

    set({ cases: initializedCases });
  },
  addCase: (caseData) => set((state) => {
    // Get all task templates for all stages
    const allTasks = Object.entries(taskTemplates)
      .map(([stageId, templates]) => 
        templates.map(template => ({
          ...template,
          id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          dueDate: typeof template.dueDate === 'string' 
            ? template.dueDate 
            : calculateDueDate(template.dueDate),
          assignedTo: caseData.assignedLawyerId,
        }))
      )
      .flat();

    const newCase: Case = {
      id: `case-${Date.now()}`,
      ...caseData,
      currentStageId: 'retainer-signed',
      documents: [],
      tasks: allTasks,
      timeline: [
        {
          id: 'tl1',
          date: new Date().toISOString(),
          description: 'Case created',
          type: 'note',
          addedBy: caseData.assignedLawyerId,
        }
      ],
    };
    return { cases: [...state.cases, newCase] };
  }),
  updateCase: (id, updates) => set((state) => {
    // If stage is changing, get new task templates
    if (updates.currentStageId) {
      const existingCase = state.cases.find(c => c.id === id);
      if (!existingCase) return state;

      // Get all task templates for all stages
      const allTasks = Object.entries(taskTemplates)
        .map(([stageId, templates]) => 
          templates.map(template => ({
            ...template,
            id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            dueDate: typeof template.dueDate === 'string' 
              ? template.dueDate 
              : calculateDueDate(template.dueDate),
            assignedTo: existingCase.assignedLawyerId,
          }))
        )
        .flat();

      updates.tasks = allTasks;
    }

    return {
      cases: state.cases.map(case_ => 
        case_.id === id ? { ...case_, ...updates } : case_
      ),
    };
  }),
}));

// Initialize the store with default cases and tasks
useCaseStore.getState().initializeStore();